/**
 * Pure math for rubber-band (drag-select-to-zoom) — the Thinkorswim-style
 * `dragAction: 'zoomRegion'` gesture (spec D7). Kept free of klinecharts/DOM
 * so it's exhaustively unit-testable; `ChartManager` supplies the pixel
 * geometry and applies the result via `zoomAtCoordinate`.
 *
 * klinecharts' `zoomAtCoordinate(scale, coordinate, animationDuration)` — with
 * `animationDuration` 0 — sets the new bar space to EXACTLY
 * `currentBarSpace * scale` (traced through `ChartImp.zoomAtCoordinate` →
 * `TimeScaleStore.zoom`, verified against
 * node_modules/klinecharts/dist/index.esm.js). So `scale` here is that same
 * multiplicative factor, not a barSpace value — `computeRubberBandZoom`'s
 * `scale` can be passed straight into `zoomAtCoordinate`.
 */

/** Below this horizontal travel, a "drag" is really a click — no zoom. */
export const MIN_DRAG_PX = 5

export interface RubberBandInput {
  /** Selection edges in container-local pixels, unordered (x0 may be > x1). */
  x0: number
  x1: number
  /** Width of the candle pane in pixels — the target the selection zooms to fill. */
  paneWidth: number
  /** The chart's current bar space (px/bar), from `chart.getBarSpace()`. */
  currentBarSpace: number
  /** klinecharts' bar-space floor (`BarSpaceLimitConstants.MIN` — 0.5 via
   *  our vendored patch; stock is 1). */
  minBarSpace: number
  /** klinecharts' bar-space ceiling (`BarSpaceLimitConstants.MAX` — 500 via
   *  our vendored patch; stock is 50). */
  maxBarSpace: number
}

export interface RubberBandResult {
  /** Multiplier for `chart.zoomAtCoordinate(scale, ...)`, clamped so
   *  `currentBarSpace * scale` stays within `[minBarSpace, maxBarSpace]`. */
  scale: number
  /** Zoom anchor x (selection center) in container-local pixels. */
  anchorX: number
  /** False when the selection is narrower than `MIN_DRAG_PX` — caller should
   *  treat the gesture as a click/no-op, not a zoom. */
  valid: boolean
}

export function computeRubberBandZoom(input: RubberBandInput): RubberBandResult {
  const { x0, x1, paneWidth, currentBarSpace, minBarSpace, maxBarSpace } = input
  const left = Math.min(x0, x1)
  const right = Math.max(x0, x1)
  const width = right - left
  const anchorX = (left + right) / 2

  if (width < MIN_DRAG_PX) {
    return { scale: 1, anchorX, valid: false }
  }

  const targetBarSpace = currentBarSpace * (paneWidth / width)
  const clampedBarSpace = Math.min(maxBarSpace, Math.max(minBarSpace, targetBarSpace))
  return { scale: clampedBarSpace / currentBarSpace, anchorX, valid: true }
}
